import { useId, type ReactElement, type ReactNode } from 'react';
import { Modal } from './Modal';
import './BottomSheet.css';

export interface BottomSheetProps {
  onClose: () => void;
  /** The heading, and the dialog's accessible name. Keep it to a few words. */
  title: string;
  /** One short line of explanation. A sheet is explanation plus actions. */
  description?: ReactNode;
  /**
   * `ui.seen` name recorded when the sheet appears (shared_web/track.ts
   * observes [data-log-seen] on nodes mounted after boot). The name must be
   * in the registry vocabulary — src/edge/analytics/metadata/registry.js.
   * A sheet has no page of its own, so this is the only way its opening is
   * measurable.
   */
  logSeen?: string;
  /**
   * Entity/context attributes recorded alongside `logSeen` (data-log-* form,
   * e.g. { 'data-log-discovery': 'home' }). They join the ui.seen dedup key,
   * so the same sheet opened from a different context records again.
   */
  logSeenAttrs?: Record<string, string>;
  className?: string;
  /** The chat's skin, for a sheet opened from a chat surface (Modal `skin`). */
  skin?: 'sand' | 'night';
  /** The actions. One row of buttons; two is the comfortable maximum. */
  children: ReactNode;
}

/**
 * The shared bottom sheet: a short question answered in place, docked to the
 * bottom edge where the thumb already is.
 *
 * It exists so an in-app door does not have to become a page. Where a full
 * screen would say a title, a line of copy and offer two buttons, this says
 * the same thing over the page the person is already on — they keep their
 * place, and Back still means what it meant before they tapped.
 *
 * Chrome only: focus handling, Escape and backdrop dismissal all stay in the
 * one dialog primitive (Modal, `sheet`), so a sheet and a centred dialog
 * behave identically for a keyboard and a screen reader.
 */
export function BottomSheet({
  onClose, title, description, logSeen, logSeenAttrs, className = '', skin, children,
}: BottomSheetProps): ReactElement {
  const titleId = useId();
  return (
    <Modal sheet onClose={onClose} labelledBy={titleId} skin={skin} className={`ember-sheet${className ? ` ${className}` : ''}`}>
      <div className="ember-sheet-inner" data-log-seen={logSeen || undefined} {...logSeenAttrs}>
        <div className="ember-sheet-head">
          <h2 id={titleId} className="ember-sheet-title">{title}</h2>
          <button type="button" className="ember-sheet-close" aria-label="Close" data-log="door_close" onClick={onClose}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        {description ? <p className="ember-sheet-copy">{description}</p> : null}
        <div className="ember-sheet-actions">{children}</div>
      </div>
    </Modal>
  );
}
